import { useState } from "react";
import { VerificationModal } from "./TaxPilotView";

// ─── Types ───────────────────────────────────────────────────────────────────
interface GstClient {
  id: string; tradeName: string; legalName: string; groupName?: string;
  regDate: string; fileNo?: string; taxpayerType: string; periodicity: string;
  gstin: string; state: string; contactPerson: string; mobile?: string;
}
type GstTopTab = "return" | "reports" | "ledgers" | "recon" | "download";
type ReturnSidebarKey = "gstr1" | "ims" | "gstr2a" | "gstr2b" | "gstr3b" | "qrmp" | "tdstcs" | "cmp08" | "gstr4" | "gstr9" | "gstr9c" | "annual-summary" | "otp-dsc" | "reg-cert";

// ─── Seed data ───────────────────────────────────────────────────────────────
const GST_CLIENTS: GstClient[] = [
  { id: "1", tradeName: "C H Infra structures", legalName: "C H INFRASTRUCTURES", regDate: "09/02/2021", taxpayerType: "Regular", periodicity: "Monthly", gstin: "29AAQFC0907Q1ZT", state: "Karnataka", contactPerson: "PULLAPPA CHAKALI" },
  { id: "2", tradeName: "Global Care Logistics", legalName: "GLOBAL CARE LOGISTICS", regDate: "22/03/2021", fileNo: "2", taxpayerType: "Regular", periodicity: "Monthly", gstin: "29AQTPP7039D1Z9", state: "Karnataka", contactPerson: "DEVARAJ PRAKASH" },
  { id: "3", tradeName: "Global Infra Conmix", legalName: "GLOBAL INFRA CONMIX", regDate: "27/03/2025", taxpayerType: "Regular", periodicity: "Monthly", gstin: "29ABCFG5263A1ZN", state: "Karnataka", contactPerson: "Balakrishna Samidisetty" },
  { id: "4", tradeName: "Haronics Consumer Products LLP", legalName: "HARONICS CONSUMER PRODUCTS LLP", regDate: "01/10/2021", taxpayerType: "Regular", periodicity: "Monthly", gstin: "29AAOFH3011L1Z7", state: "Karnataka", contactPerson: "Vasanthakumar Sannenahalli" },
  { id: "5", tradeName: "Icon Infra", legalName: "ICON INFRA", regDate: "10/10/2025", taxpayerType: "Regular", periodicity: "Monthly", gstin: "29AALFI5968E1ZU", state: "Karnataka", contactPerson: "Raju Ranjith Sherigar" },
  { id: "6", tradeName: "Initiumnet Technology Private Limited", legalName: "INITIUMNET TECHNOLOGY PRIVATE LIMITED", regDate: "25/02/2026", taxpayerType: "Regular", periodicity: "Monthly", gstin: "29AAICI5339K1Z1", state: "Karnataka", contactPerson: "Santosh Kumar Padhy" },
  { id: "7", tradeName: "MDF Enterprises", legalName: "MDF ENTERPRISES", regDate: "22/08/2022", fileNo: "3", taxpayerType: "Regular", periodicity: "Monthly", gstin: "29DDSPK6142P1ZK", state: "Karnataka", contactPerson: "Subhanasab Khadaranavar" },
  { id: "8", tradeName: "SRS Infrastructures", legalName: "SRS INFRASTRUCTURES", regDate: "13/05/2026", taxpayerType: "Regular", periodicity: "Monthly", gstin: "29AFWFS7459G1ZX", state: "Karnataka", contactPerson: "Devarahally Muniyappa Suresh" },
  { id: "9", tradeName: "Unitech Concrete", legalName: "UNITECH CONCRETE", regDate: "27/09/2017", taxpayerType: "Regular", periodicity: "Monthly", gstin: "29AAFFU0397M2ZH", state: "Karnataka", contactPerson: "Chinnakotla Ramesh Babu" },
  { id: "10", tradeName: "Vaarahi Concretes", legalName: "VAARAHI CONCRETES", regDate: "11/05/2026", taxpayerType: "Regular", periodicity: "Monthly", gstin: "29BNNPP0141C1Z5", state: "Karnataka", contactPerson: "Pullappa Chakali" },
];

const FY_OPTIONS = ["2024 - 2025", "2025 - 2026", "2026 - 2027"];
const MONTH_OPTIONS = ["April", "May", "June", "July", "August", "September", "October", "November", "December", "January", "February", "March"];

const RETURN_SIDEBAR: Array<{ key: ReturnSidebarKey; label: string; badge?: string }> = [
  { key: "gstr1", label: "GSTR-1 / IFF" },
  { key: "ims", label: "IMS", badge: "NEW" },
  { key: "gstr2a", label: "GSTR-2A" },
  { key: "gstr2b", label: "GSTR-2B" },
  { key: "gstr3b", label: "GSTR-3B" },
  { key: "qrmp", label: "QRMP Taxpayable" },
  { key: "tdstcs", label: "TDS & TCS" },
  { key: "cmp08", label: "CMP-08" },
  { key: "gstr4", label: "GSTR-4 [Annual]" },
  { key: "gstr9", label: "GSTR-9" },
  { key: "gstr9c", label: "GSTR-9C" },
  { key: "annual-summary", label: "Annual Summary Books Vs. Return" },
  { key: "otp-dsc", label: "OTP / DSC Filing" },
  { key: "reg-cert", label: "Download Registration Certificate" },
];

// ─── Root ────────────────────────────────────────────────────────────────────
export function GstReturnsView({ onBack }: { onBack: () => void }) {
  const [clients] = useState<GstClient[]>(GST_CLIENTS);
  const [pendingClient, setPendingClient] = useState<GstClient | null>(null);
  const [fy, setFy] = useState("2026 - 2027");
  const [month, setMonth] = useState("July");
  const [active, setActive] = useState<{ client: GstClient; fy: string; month: string } | null>(null);

  if (!active) {
    return (
      <>
        <GstClientList clients={clients} onOpen={setPendingClient} onBack={onBack} />
        {pendingClient && (
          <GstPeriodModal
            client={pendingClient}
            fy={fy} month={month}
            onChangeFy={setFy} onChangeMonth={setMonth}
            onGo={() => { setActive({ client: pendingClient, fy, month }); setPendingClient(null); }}
            onExit={() => setPendingClient(null)}
          />
        )}
      </>
    );
  }

  return (
    <GstWorkspace
      client={active.client} fy={active.fy} month={active.month}
      onExitClient={() => setActive(null)}
      onBackToTaxPilot={onBack}
    />
  );
}

// ─── Client list ─────────────────────────────────────────────────────────────
function GstClientList({ clients, onOpen, onBack }: { clients: GstClient[]; onOpen: (c: GstClient) => void; onBack: () => void }) {
  return (
    <div className="tp-tds-shell">
      <div className="tp-tds-topbar">
        <div className="tp-tds-topbar-left">
          <button className="tp-back-link" onClick={onBack} type="button">← TaxPilot</button>
          <div className="tp-tds-form-selector"><span>Module :</span><strong>GST Returns — Client List</strong></div>
        </div>
        <div className="tp-tds-topbar-right">
          <button className="tp-btn-secondary sm" type="button">Bulk Import/Export</button>
          <button className="tp-btn-primary sm" type="button">+ Add New</button>
        </div>
      </div>
      <div className="gst-client-list-wrap">
        <table className="gst-client-table">
          <thead>
            <tr>
              <th>S.No</th><th>Trade Name</th><th>Group</th><th>Registration Date</th><th>File No</th>
              <th>Taxpayer Type</th><th>Periodicity</th><th>GSTIN</th><th>State</th><th>Contact Person</th><th>Mobile</th>
            </tr>
          </thead>
          <tbody>
            {clients.map((c, i) => (
              <tr key={c.id}>
                <td>{i + 1}</td>
                <td><button className="gst-client-name-link" onClick={() => onOpen(c)} type="button">{c.tradeName}</button></td>
                <td>{c.groupName || ""}</td>
                <td>{c.regDate}</td>
                <td>{c.fileNo || ""}</td>
                <td>{c.taxpayerType}</td>
                <td>{c.periodicity}</td>
                <td className="gst-mono">{c.gstin}</td>
                <td>{c.state}</td>
                <td>{c.contactPerson}</td>
                <td>{c.mobile || <span className="tp-muted">Enter mobile</span>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── Period picker modal ───────────────────────────────────────────────────────
function GstPeriodModal({ client, fy, month, onChangeFy, onChangeMonth, onGo, onExit }: {
  client: GstClient; fy: string; month: string; onChangeFy: (v: string) => void; onChangeMonth: (v: string) => void; onGo: () => void; onExit: () => void;
}) {
  return (
    <div className="tp-modal-backdrop" onClick={onExit}>
      <div className="tp-modal" style={{ maxWidth: 420 }} onClick={e => e.stopPropagation()}>
        <div className="tp-modal-header">
          <div className="tp-modal-title-row">
            <div className="tp-modal-icon">📅</div>
            <div><h3>Select Tax Period</h3></div>
          </div>
          <button className="tp-modal-close" onClick={onExit} type="button">✕</button>
        </div>
        <div className="tp-modal-body">
          <div className="gst-period-row"><span>Trade Name</span><strong>{client.tradeName}</strong></div>
          <div className="gst-period-row"><span>Legal Name</span><strong>{client.legalName}</strong></div>
          <div className="gst-period-row"><span>GSTIN</span><strong>{client.gstin}</strong></div>
          <div className="gst-period-row"><span>State</span><strong>{client.state}</strong></div>
          <div className="tp-form-field" style={{ marginTop: 12 }}>
            <label>F.Y.</label>
            <select className="tp-input" value={fy} onChange={e => onChangeFy(e.target.value)}>
              {FY_OPTIONS.map(f => <option key={f}>{f}</option>)}
            </select>
          </div>
          <div className="tp-form-field" style={{ marginTop: 10 }}>
            <label>Month</label>
            <select className="tp-input" value={month} onChange={e => onChangeMonth(e.target.value)}>
              {MONTH_OPTIONS.map(m => <option key={m}>{m}</option>)}
            </select>
          </div>
          <div className="tp-modal-actions">
            <button className="tp-btn-secondary" onClick={onExit} type="button">Exit</button>
            <button className="tp-btn-primary" onClick={onGo} type="button">Go →</button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Workspace shell ─────────────────────────────────────────────────────────
function GstWorkspace({ client, fy, month, onExitClient, onBackToTaxPilot }: {
  client: GstClient; fy: string; month: string; onExitClient: () => void; onBackToTaxPilot: () => void;
}) {
  const [topTab, setTopTab] = useState<GstTopTab>("return");
  const [returnView, setReturnView] = useState<"summary" | ReturnSidebarKey>("summary");
  const [ledgerKey, setLedgerKey] = useState("cash");
  const [downloadTab, setDownloadTab] = useState<"quick" | "json">("quick");
  const [showOnlineTools, setShowOnlineTools] = useState(false);
  const [showPortalLogin, setShowPortalLogin] = useState(false);
  const [portalConnected, setPortalConnected] = useState(false);

  const TOP_TABS: Array<{ key: GstTopTab; label: string }> = [
    { key: "return", label: "Return Dashboard" },
    { key: "reports", label: "Reports Dashboard" },
    { key: "ledgers", label: "Electronic Ledgers" },
    { key: "recon", label: "Reconciliation Dashboard" },
    { key: "download", label: "Download From GST Portal" },
  ];

  return (
    <div className="tp-tds-shell">
      <div className="tp-tds-topbar">
        <div className="tp-tds-topbar-left">
          <button className="tp-back-link" onClick={onExitClient} type="button">← Client List</button>
          <div className="tp-tds-meta">
            <strong>{client.tradeName}</strong>
            <span className="tp-muted">· {client.gstin} · {client.state} · Reg {client.regDate}</span>
          </div>
        </div>
        <div className="tp-tds-topbar-right">
          <span className="gst-period-chip">{month} · {fy}</span>
          <button className="tp-btn-secondary sm" onClick={() => setShowPortalLogin(true)} type="button">
            {portalConnected ? "Portal Connected ✓" : "GST Portal Login"}
          </button>
          <button className="tp-btn-secondary sm" onClick={() => setShowOnlineTools(true)} type="button">Online Tools</button>
          <button className="tp-back-link" onClick={onBackToTaxPilot} type="button">TaxPilot ↗</button>
        </div>
      </div>

      <div className="gst-toptabs">
        {TOP_TABS.map(t => (
          <button key={t.key} className={`gst-toptab ${topTab === t.key ? "active" : ""}`} onClick={() => setTopTab(t.key)} type="button">{t.label}</button>
        ))}
      </div>

      <div className="gst-workspace-body">
        {topTab === "return" && (
          <div className="gst-return-layout">
            <aside className="gst-return-sidebar">
              {RETURN_SIDEBAR.map(item => (
                <button key={item.key} className={`gst-return-nav-btn ${returnView === item.key ? "active" : ""}`} onClick={() => setReturnView(item.key)} type="button">
                  <span>{item.label}</span>
                  {item.badge && <span className="gst-nav-badge">{item.badge}</span>}
                </button>
              ))}
            </aside>
            <div className="gst-return-main">
              {returnView === "summary" && <GstReturnDashboardSummary onOpenGstr1={() => setReturnView("gstr1")} month={month} />}
              {returnView === "gstr1" && <Gstr1ReturnPage client={client} fy={fy} month={month} onBackToSummary={() => setReturnView("summary")} />}
              {returnView !== "summary" && returnView !== "gstr1" && (
                <GstSpecPendingPanel label={RETURN_SIDEBAR.find(r => r.key === returnView)?.label || ""} />
              )}
            </div>
          </div>
        )}
        {topTab === "reports" && <GstReportsDashboard />}
        {topTab === "ledgers" && <GstElectronicLedgers ledgerKey={ledgerKey} onSelectLedger={setLedgerKey} />}
        {topTab === "recon" && <GstReconciliationDashboard />}
        {topTab === "download" && <GstDownloadPortal downloadTab={downloadTab} onTabChange={setDownloadTab} fy={fy} />}
      </div>

      {showOnlineTools && <GstOnlineToolsModal onClose={() => setShowOnlineTools(false)} />}
      {showPortalLogin && (
        <VerificationModal
          onClose={() => setShowPortalLogin(false)}
          onSuccess={() => { setShowPortalLogin(false); setPortalConnected(true); }}
          title="Connect GST Portal"
          subtitle="Authenticate with the GST Common Portal to sync returns and ledgers for this client."
          ackPrefix="GSTCONN"
          successHeadline="GST Portal connected"
          successBody={`${client.tradeName}'s GST portal session is now linked for ${month} ${fy}.`}
        />
      )}
    </div>
  );
}

// ─── Return Dashboard summary ──────────────────────────────────────────────────
const GSTR1_SUMMARY_ROWS = [
  "B2B Invoices", "B2CL Invoices", "B2CS Invoices", "Cr/Dr Note Registered Taxable Value", "Cr/Dr Note Un Reg. Taxable Value",
  "Advance Received Taxable Value", "Advance Adjusted Taxable Value", "Nil rated, exempted & Non GST Supply", "Zero rated supplies",
];
const GSTR3B_SUMMARY_ROWS = [
  "Taxable Outward Supply", "Tax Payable on Outward Supply", "Taxable Value under RCM", "Tax on RCM", "Zero Rated Supplies",
  "Tax on Zero Rated Supply", "Nil rated & exempted Supply", "Non GST Supply",
];
const RETURN_CALENDAR_PERIODS = ["Feb", "Mar", "Apr", "May", "Jun"];

function GstReturnDashboardSummary({ onOpenGstr1, month }: { onOpenGstr1: () => void; month: string }) {
  return (
    <div className="gst-summary-grid">
      <div className="gst-summary-card">
        <div className="gst-summary-card-header"><h3>GSTR-1 Comparative Summary</h3><button className="tp-btn-primary sm" onClick={onOpenGstr1} type="button">View Summary</button></div>
        <table className="gst-compare-table">
          <thead><tr><th>Particulars</th><th>{month} [Rs.]</th><th>Prior month [Rs.]</th></tr></thead>
          <tbody>{GSTR1_SUMMARY_ROWS.map(r => <tr key={r}><td>{r}</td><td>0.00</td><td>0.00</td></tr>)}</tbody>
        </table>
      </div>
      <div className="gst-summary-card">
        <div className="gst-summary-card-header"><h3>GSTR-3B Comparative Summary</h3><button className="tp-btn-primary sm" type="button">View Summary</button></div>
        <table className="gst-compare-table">
          <thead><tr><th>Particulars</th><th>{month} [Rs.]</th><th>Prior month [Rs.]</th></tr></thead>
          <tbody>{GSTR3B_SUMMARY_ROWS.map(r => <tr key={r}><td>{r}</td><td>0.00</td><td>0.00</td></tr>)}</tbody>
        </table>
      </div>
      <div className="gst-summary-status-row">
        <button className="tp-btn-primary sm" type="button">Get Last 5 Return Status</button>
        <span className="tp-muted">Last Updated: —</span>
        <span className="tp-pan-status valid">ACTIVE</span>
      </div>
      <div className="gst-summary-card">
        <h3>Return Calendar (Last 5 Return Periods)</h3>
        <div className="gst-calendar-rows">
          {["GSTR-1 / IFF", "GSTR-3B"].map(row => (
            <div key={row} className="gst-calendar-row">
              <div className="gst-calendar-row-label">{row}</div>
              {RETURN_CALENDAR_PERIODS.map(p => (
                <div key={p} className="gst-calendar-cell"><span>{p}</span><strong className="tp-success-text">Filed</strong></div>
              ))}
            </div>
          ))}
        </div>
      </div>
      <div className="gst-summary-two-col">
        <div className="gst-summary-card">
          <h3>Useful Tools / Links</h3>
          <div className="gst-link-list">
            {["Notice and Order", "CBIC Notification", "Late Fees/Interest Calculator", "Additional Notice and Orders", "CBIC Circulars and Orders", "Add New Task"].map(l => (
              <a key={l} className="gst-inline-link" href="#" onClick={e => e.preventDefault()}>{l}</a>
            ))}
          </div>
        </div>
        <div className="gst-summary-card">
          <h3>Annual Return Applicability</h3>
          <table className="gst-compare-table">
            <tbody>
              <tr><td>GSTIN Turnover</td><td>N/A</td></tr>
              <tr><td>PAN Turnover</td><td>N/A</td></tr>
              <tr><td>GSTR-9</td><td>N/A</td></tr>
              <tr><td>GSTR-9C</td><td>N/A</td></tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ─── GSTR-1 return page ─────────────────────────────────────────────────────
const GSTR1_INVOICE_ROWS: Array<{ label: string; table: string }> = [
  { label: "B2B Invoices", table: "Table No.4" },
  { label: "B2CL Large Invoices", table: "Table No.5" },
  { label: "B2CS Small Invoices", table: "Table No.7" },
  { label: "Nil rated supplies", table: "Table No.6A" },
  { label: "Cr/Dr Notes (Registered)", table: "Table No.9B" },
  { label: "Cr/Dr Notes (Unregistered)", table: "Table No.9B" },
  { label: "Nil rated, exempted and non GST outward supplies", table: "Table No.8" },
  { label: "Advance Received", table: "Table No.11A" },
  { label: "Advance Adjusted", table: "Table No.11B" },
];
const GSTR1_EXTRA_ROWS = ["HSN Summary", "Documents Issued Summary", "Supply Through E-Commerce Operator [Table No.14(a) & 14(b)]"];

function Gstr1ReturnPage({ client, fy, month, onBackToSummary }: { client: GstClient; fy: string; month: string; onBackToSummary: () => void }) {
  const [showFileModal, setShowFileModal] = useState(false);
  const [filed, setFiled] = useState(false);
  const [arn, setArn] = useState("");

  return (
    <div className="gst-gstr1-page">
      <div className="gst-gstr1-header">
        <div>
          <button className="text-button" onClick={onBackToSummary} type="button">← Return Dashboard</button>
          <h2>GSTR-1 Return — {month} {fy.split(" - ")[0]}</h2>
        </div>
        <div className="gst-gstr1-status-block">
          <div><span>Status</span><strong className={filed ? "tp-success-text" : ""}>{filed ? "Filed" : "Not Checked"}</strong></div>
          <div><span>Date of Filing</span><strong>{filed ? new Date().toLocaleDateString("en-IN") : "-"}</strong></div>
          <div><span>ARN</span><strong>{filed ? arn : "-"}</strong></div>
          <button className="tp-btn-primary sm" onClick={() => setShowFileModal(true)} type="button" disabled={filed}>{filed ? "Filed ✓" : "File GSTR-1"}</button>
          <button className="tp-btn-secondary sm" type="button">Get Status</button>
          <button className="tp-btn-secondary sm" type="button">Download GSTR-1</button>
        </div>
      </div>

      <table className="gst-invoice-table">
        <thead>
          <tr><th>Type of Invoices</th><th>Action</th><th>Validation</th><th>No of Records</th><th>Taxable Value</th><th>IGST</th><th>CGST</th><th>SGST</th><th>Cess</th><th>Total GST</th></tr>
        </thead>
        <tbody>
          {GSTR1_INVOICE_ROWS.map(r => (
            <tr key={r.label}>
              <td>{r.label} <span className="tp-muted">[{r.table}]</span></td>
              <td><a className="gst-inline-link" href="#" onClick={e => e.preventDefault()}>View</a></td>
              <td>-</td><td>0</td><td>0</td><td>0</td><td>0</td><td>0</td><td>0</td><td>0</td>
            </tr>
          ))}
          {GSTR1_EXTRA_ROWS.map(r => (
            <tr key={r}>
              <td>{r}</td>
              <td><a className="gst-inline-link" href="#" onClick={e => e.preventDefault()}>View</a></td>
              <td colSpan={8} />
            </tr>
          ))}
        </tbody>
      </table>

      <div className="gst-gstr1-actions">
        {["Import Data", "Export Json", "Upload Json (All)", "Upload Json (Section Wise)", "Json Upload Status", "Reconcile Before File"].map(a => (
          <button key={a} className="tp-btn-secondary sm" type="button">{a}</button>
        ))}
      </div>

      <h3 style={{ marginTop: 20, marginBottom: 10, fontFamily: "'Nunito',sans-serif", fontWeight: 800, fontSize: 14, color: "#1a2341" }}>Comparative Summary of GSTR-1 and GSTR-3B</h3>
      <table className="gst-compare-wide-table">
        <thead>
          <tr><th>Return Type</th><th>Outward Supply Value</th><th>Zero Rated Supply</th><th>Nil + Exempted</th><th>Total Supply</th><th>IGST</th><th>CGST</th><th>SGST</th><th>CESS</th><th>IGST on Export</th><th>Total GST</th></tr>
        </thead>
        <tbody>
          {["GSTR-3B", "GSTR-1", "Difference"].map(r => (
            <tr key={r}><td><strong>{r}</strong></td>{Array.from({ length: 9 }).map((_, i) => <td key={i}>0.00</td>)}</tr>
          ))}
        </tbody>
      </table>

      {showFileModal && (
        <VerificationModal
          onClose={() => setShowFileModal(false)}
          onSuccess={() => { setArn("AA29" + Math.floor(Math.random() * 900000000 + 100000000)); setFiled(true); setShowFileModal(false); }}
          title="File GSTR-1 Return"
          subtitle={`Filing GSTR-1 for ${client.tradeName} — ${month} ${fy}. Verification is required to submit to the GST Common Portal.`}
          ackPrefix="AA29"
          successHeadline="GSTR-1 filed successfully"
          successBody={`GSTR-1 for ${client.tradeName}, ${month} ${fy}, has been filed on the GST portal.`}
        />
      )}
    </div>
  );
}

// ─── Reports Dashboard ───────────────────────────────────────────────────────
const REPORT_GROUPS: Array<{ title: string; items: string[] }> = [
  { title: "GSTR-1 Reports", items: ["GSTR-1 Yearly Sales Summary", "GSTR-1 Rate wise Sales", "Export Full Year GSTR-1 in Excel", "GSTR-1 Party Wise Sale Invoices", "Auto-Populated E-Invoice Data"] },
  { title: "GSTR-3B Reports", items: ["GSTR-3B Yearly Sales Summary", "GSTR-3B Yearly ITC Summary", "GSTR-3B Tax Liability Offset", "Export Full Year GSTR-3B in Excel", "GSTR-3B vs. GSTR-2B ITC (Portal report)"] },
  { title: "GSTR-2A/4A Reports", items: ["GSTR-2A Rate wise Purchases", "GSTR-2A Full Year Data", "GSTR-2A Party Wise Purchase Invoices"] },
  { title: "GSTR-2B Reports", items: ["GSTR-2B Rate wise Purchases", "GSTR-2B Full Year Data", "GSTR-2B Party Wise Purchase Invoices", "GSTR-2B Monthly Summary"] },
  { title: "Comparison Reports", items: ["GSTR-3B vs. GSTR-2A ITC", "3B Vs 2B Detailed Analysis Report", "RCM ITC vs. RCM Tax Paid", "ITC Claimed vs. ITC Utilized", "GSTR-3B Vs. GSTR-1 (Compare Software Data)", "GSTR-3B Vs. GSTR-1 (Quick Compare)"] },
  { title: "MIS Reports", items: ["Challan Deposited During the Year", "Return Filed During the Year", "Interest and Late Fees Paid Through GSTR-3B Return", "Interest Calculation on Delayed Payment", "View Notice & Orders"] },
  { title: "GST Portal Report", items: ["Credit and Liability Statement", "Liability Other Than Export/Reverse Charge", "Liability Due to Reverse Charge", "ITC Credit Claimed & Due"] },
  { title: "Refund Reports", items: ["Refund Application Filed on Portal", "Refund Claimed From ITC Ledger", "Refund Claimed From Cash Ledger"] },
  { title: "DRC-03 Reports", items: ["DRC-03 Filed on Portal", "Voluntary Payment From Cash Ledger", "Voluntary Payment From Credit Ledger"] },
];

function GstReportsDashboard() {
  const [tab, setTab] = useState<"regular" | "misc">("regular");
  return (
    <div className="gst-reports-wrap">
      <div className="gst-toptabs gst-subtabs">
        <button className={`gst-toptab ${tab === "regular" ? "active" : ""}`} onClick={() => setTab("regular")} type="button">Regular Reports</button>
        <button className={`gst-toptab ${tab === "misc" ? "active" : ""}`} onClick={() => setTab("misc")} type="button">Miscellaneous Reports</button>
      </div>
      {tab === "regular" ? (
        <div className="gst-reports-grid">
          {REPORT_GROUPS.map(g => (
            <div key={g.title} className="gst-summary-card">
              <h3>{g.title}</h3>
              <div className="gst-link-list">{g.items.map(i => <a key={i} className="gst-inline-link" href="#" onClick={e => e.preventDefault()}>{i}</a>)}</div>
            </div>
          ))}
        </div>
      ) : <GstSpecPendingPanel label="Miscellaneous Reports" />}
    </div>
  );
}

// ─── Electronic Ledgers ──────────────────────────────────────────────────────
const LEDGER_NAV = [
  { key: "cash", label: "Cash Ledger" },
  { key: "credit", label: "Credit Ledger" },
  { key: "liability", label: "Liability Ledger" },
  { key: "liab2", label: "Electronic Liability Register (Part-II)" },
  { key: "credit-reversal", label: "Credit Reversal and Re-claimed Statement" },
  { key: "pmt09", label: "File PMT09" },
  { key: "rcm", label: "RCM Liability/ITC Statement" },
  { key: "neg-liab", label: "Negative liability statement" },
];

function GstElectronicLedgers({ ledgerKey, onSelectLedger }: { ledgerKey: string; onSelectLedger: (k: string) => void }) {
  const [syncing, setSyncing] = useState(false);
  const [synced, setSynced] = useState(false);
  const currentLabel = LEDGER_NAV.find(l => l.key === ledgerKey)?.label || "Cash Ledger";
  const rows = ["Electronic Credit Ledger Balance", "Reversal and Reclaimed Balance", "RCM Liability/ITC Statement"];

  return (
    <div className="gst-return-layout">
      <aside className="gst-return-sidebar">
        {LEDGER_NAV.map(l => (
          <button key={l.key} className={`gst-return-nav-btn ${ledgerKey === l.key ? "active" : ""}`} onClick={() => { onSelectLedger(l.key); setSynced(false); }} type="button"><span>{l.label}</span></button>
        ))}
      </aside>
      <div className="gst-return-main">
        <div className="gst-summary-card-header"><h3>Electronic Cash & Credit Live Ledger Balance — {currentLabel}</h3></div>
        <table className="gst-compare-wide-table">
          <thead><tr><th>Description</th><th>CGST</th><th>SGST</th><th>IGST</th><th>CESS</th><th>Total</th></tr></thead>
          <tbody>
            {rows.map(r => (
              <tr key={r}>
                <td>{r} — Last Sync :: {synced ? "Just now" : "Not Synced"}</td>
                <td>{synced ? "0.00" : ""}</td><td>{synced ? "0.00" : ""}</td><td>{synced ? "0.00" : ""}</td><td>{synced ? "0.00" : ""}</td><td>{synced ? "0.00" : ""}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <button className="tp-btn-primary sm" style={{ marginTop: 16 }} onClick={() => { setSyncing(true); setTimeout(() => { setSyncing(false); setSynced(true); }, 1000); }} disabled={syncing} type="button">
          {syncing ? "Fetching…" : "Get Live Balance"}
        </button>
      </div>
    </div>
  );
}

// ─── Reconciliation Dashboard ────────────────────────────────────────────────
const RECON_CARDS = [
  { key: "sale-1", label: "Sale Vs GSTR-1 Reconciliation", color: "orange" },
  { key: "purchase-2a", label: "Purchase Vs. GSTR-2A Reconciliation", color: "green" },
  { key: "purchase-2b", label: "Purchase Vs. GSTR-2B Reconciliation", color: "red" },
  { key: "purchase-9-8a", label: "Purchase Vs. GSTR-9 Table-8A Reconciliation", color: "blue" },
  { key: "2b-2a", label: "2B Vs. 2A Reconciliation", color: "slate" },
  { key: "9-8a-2b", label: "GSTR-9 Table 8A Vs. GSTR-2B Reconciliation", color: "red" },
];

function GstReconciliationDashboard() {
  const [openPanel, setOpenPanel] = useState<string | null>(null);
  return (
    <div className="gst-recon-wrap">
      <div className="gst-recon-grid">
        {RECON_CARDS.map(c => (
          <button key={c.key} className={`gst-recon-card gst-recon-${c.color}`} onClick={() => setOpenPanel(c.key)} type="button">
            <span className="gst-recon-icon">⚖</span>
            <strong>{c.label}</strong>
          </button>
        ))}
      </div>
      {openPanel && <GstSpecPendingPanel label={RECON_CARDS.find(c => c.key === openPanel)?.label || ""} onClose={() => setOpenPanel(null)} />}
    </div>
  );
}

// ─── Download From GST Portal ────────────────────────────────────────────────
const DOWNLOAD_ROWS = ["GSTR-1", "Update GSTR-1 Party Name", "GSTR-1A", "GSTR-2A", "Update GSTR-2A Supplier Name", "GSTR-2B", "GSTR-3B", "GST TDS & TCS"];
const DOWNLOAD_MONTHS = ["April", "May", "June", "July", "August", "September", "October", "November", "December", "January", "February", "March"];

function GstDownloadPortal({ downloadTab, onTabChange, fy }: { downloadTab: "quick" | "json"; onTabChange: (t: "quick" | "json") => void; fy: string }) {
  return (
    <div className="gst-download-wrap">
      <div className="gst-toptabs gst-subtabs">
        <button className={`gst-toptab ${downloadTab === "quick" ? "active" : ""}`} onClick={() => onTabChange("quick")} type="button">Quick Download</button>
        <button className={`gst-toptab ${downloadTab === "json" ? "active" : ""}`} onClick={() => onTabChange("json")} type="button">Json Download</button>
      </div>
      {downloadTab === "quick" ? (
        <>
          <div className="gst-summary-card-header"><h3>Quick Download Data From GST Portal — F.Y. {fy}</h3><button className="tp-btn-primary sm" type="button">Download Portal Data</button></div>
          <div className="gst-download-table-wrap">
            <table className="gst-download-table">
              <thead><tr><th>Description</th>{DOWNLOAD_MONTHS.map(m => <th key={m}>{m.slice(0, 3)}</th>)}</tr></thead>
              <tbody>
                {DOWNLOAD_ROWS.map(r => (
                  <tr key={r}>
                    <td>{r}</td>
                    {DOWNLOAD_MONTHS.map(m => <td key={m} className="tp-muted">{["GSTR-1", "GSTR-3B"].includes(r) ? "Not Filed" : ""}</td>)}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="gst-export-row">
            <span>Export To Excel:</span>
            {["GSTR-1", "GSTR-1A", "GSTR-2A", "GSTR-2B", "GSTR-3B", "TDS-TCS Report"].map(o => (
              <label key={o} className="gst-radio-label"><input name="gst-export" type="radio" /> {o}</label>
            ))}
            <button className="tp-btn-primary sm" type="button">Export Selected Return Excel</button>
          </div>
        </>
      ) : <GstSpecPendingPanel label="Json Download" />}
    </div>
  );
}

// ─── Online Tools modal ──────────────────────────────────────────────────────
const ONLINE_TOOLS: Array<[string, string]> = [
  ["Verify GSTIN", "Bulk GSTIN Verification"],
  ["View GSTR-1 Error File", "Download All Return PDF from Portal"],
  ["Create Challan", "GST Portal Interest Calculator report"],
  ["PMT-09", "Taxpayer Communication"],
  ["File DRC03", "GSTIN Verification - New"],
];

function GstOnlineToolsModal({ onClose }: { onClose: () => void }) {
  return (
    <div className="tp-modal-backdrop" onClick={onClose}>
      <div className="tp-modal" style={{ maxWidth: 540 }} onClick={e => e.stopPropagation()}>
        <div className="tp-modal-header">
          <div className="tp-modal-title-row"><div className="tp-modal-icon">🛠</div><div><h3>Online Tools</h3></div></div>
          <button className="tp-modal-close" onClick={onClose} type="button">✕</button>
        </div>
        <div className="tp-modal-body">
          <div className="gst-tools-grid">
            {ONLINE_TOOLS.map((pair, i) => (
              <div key={i} className="gst-tools-row">
                <a className="gst-inline-link" href="#" onClick={e => e.preventDefault()}>{pair[0]}</a>
                <a className="gst-inline-link" href="#" onClick={e => e.preventDefault()}>{pair[1]}</a>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Generic spec-pending panel (reused across not-yet-scoped screens) ────────
function GstSpecPendingPanel({ label, onClose }: { label: string; onClose?: () => void }) {
  return (
    <div className="tp-gst-placeholder">
      {onClose && <button className="tp-modal-close" style={{ position: "absolute", top: 8, right: 8 }} onClick={onClose} type="button">✕</button>}
      <div className="tp-gst-placeholder-icon">🧩</div>
      <h2>{label}</h2>
      <p>Screens for this one are pending — send the reference whenever it's ready and it'll be built out to match, the same way GSTR-1 was.</p>
    </div>
  );
}
